import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { readSseStream, splitFailureDetail } from "../lib/sseClient";
import { useAdminAuth } from "./useAdminAuth";

export type StreamPhase = "commits" | "ai" | "saving" | null;

export interface StreamUsage {
  inputTokens?: number;
  outputTokens?: number;
}

export interface PartialEnvelope {
  title?: string;
  description?: string;
  sections?: Array<{ kind?: string; items?: Array<string | undefined> }>;
  tags?: Array<string | undefined>;
}

export interface StreamState {
  isRunning: boolean;
  tag: string | null;
  phase: StreamPhase;
  provider: string | null;
  markdown: string;
  envelope: PartialEnvelope | null;
  usage: StreamUsage | null;
  error: string | null;
  errorDetail: string | null;
  notices: string[];
  completed: string[];
}

const initialState: StreamState = {
  isRunning: false,
  tag: null,
  phase: null,
  provider: null,
  markdown: "",
  envelope: null,
  usage: null,
  error: null,
  errorDetail: null,
  notices: [],
  completed: [],
};

function readTagFromSearch(search: string): string | null {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search
  );
  return params.get("tag");
}

const asString = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

function reduce(prev: StreamState, event: Record<string, unknown>): StreamState {
  const tag = asString(event.tag) ?? prev.tag;

  switch (event.type) {
    case "phase":
      return { ...prev, tag, phase: event.phase as StreamPhase };
    case "provider":
      return {
        ...prev,
        tag,
        provider: (event.provider as { name?: string })?.name ?? prev.provider,
      };
    case "partial":
      return { ...prev, tag, envelope: event.envelope as PartialEnvelope };
    case "done":
      return {
        ...prev,
        tag,
        phase: null,
        markdown: asString(event.markdown) ?? prev.markdown,
        envelope: (event.envelope as PartialEnvelope) ?? prev.envelope,
        usage: (event.usage as StreamUsage) ?? prev.usage,
        provider: asString(event.provider) ?? prev.provider,
        completed: tag ? [...prev.completed, tag] : prev.completed,
      };
    case "skipped":
      return {
        ...prev,
        tag,
        phase: null,
        notices: [...prev.notices, asString(event.reason) ?? "Geração pulada."],
      };
    case "error": {
      const split = splitFailureDetail(
        asString(event.message) ?? "Falha na geração."
      );
      return { ...prev, tag, error: split.message, errorDetail: split.detail };
    }
    default:
      return prev;
  }
}

export const useStreamRunner = (onDone?: () => void) => {
  const { token } = useAdminAuth();
  const [state, setState] = useState<StreamState>(initialState);
  const controllerRef = useRef<AbortController | null>(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  const abort = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setState((prev) => ({ ...prev, isRunning: false, phase: null }));
  }, []);

  const start = useCallback(
    async (search: string, body?: unknown) => {
      if (!token) return;

      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setState({
        ...initialState,
        isRunning: true,
        tag: readTagFromSearch(search),
      });

      try {
        await readSseStream({
          url: `/api/admin/stream${search}`,
          token,
          body,
          signal: controller.signal,
          onEvent: (raw) => {
            if (controllerRef.current !== controller) return;
            const event = raw as Record<string, unknown>;
            if (event.type === "done") doneRef.current?.();
            setState((prev) => reduce(prev, event));
          },
        });
      } catch (error) {
        if (
          controllerRef.current === controller &&
          (error as Error).name !== "AbortError"
        ) {
          setState((prev) => ({ ...prev, error: (error as Error).message }));
        }
      } finally {
        if (controllerRef.current === controller) {
          controllerRef.current = null;
          setState((prev) => ({ ...prev, isRunning: false, phase: null }));
        }
      }
    },
    [token]
  );

  return { ...state, start, abort };
};

export type StreamRunner = StreamState & {
  start: (search: string, body?: unknown) => Promise<void>;
  abort: () => void;
};

const GenerationStreamContext = createContext<StreamRunner | null>(null);

export const GenerationStreamProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const queryClient = useQueryClient();

  const onDone = useCallback(() => {
    queryClient.invalidateQueries("admin-releases");
    queryClient.invalidateQueries("changelog");
    queryClient.invalidateQueries("stats");
    toast.success("Changelog atualizado");
  }, [queryClient]);

  const runner = useStreamRunner(onDone);
  const shownError = useRef<string | null>(null);

  useEffect(() => {
    if (!runner.error) {
      shownError.current = null;
      return;
    }
    if (runner.error === shownError.current) return;
    shownError.current = runner.error;
    toast.error(runner.error);
  }, [runner.error]);

  return (
    <GenerationStreamContext.Provider value={runner}>
      {children}
    </GenerationStreamContext.Provider>
  );
};

export const useGenerationStream = (): StreamRunner => {
  const ctx = useContext(GenerationStreamContext);
  if (!ctx) {
    throw new Error(
      "useGenerationStream deve ser usado dentro de GenerationStreamProvider"
    );
  }
  return ctx;
};
